import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToasterService, ToasterConfig } from 'angular2-toaster';
import { CookieService } from 'angular2-cookie/core';

import { FormatUtilities } from '../utilities/format-utilities';
import { LoadingSpinnerService } from '../services/loading-spinner.service';
import { PageTitleService } from '../services/page-title.service';
import { AuditHistoryService } from '../services/audit-history.service';
import { QuoteInfoService } from '../services/quote-info.service';
import { ApplicationStateService } from '../services/application-state.service';
import { QuoteService } from '../services/quote.service';
import { CurrentUserService } from '../services/current-user.service';
import { ModalDialogService } from '../reusable-components/modal-dialog/modal-dialog.service';
import { PagerService } from '../services/pager.service';
import { BrowserWidthService } from '../services/browser-width.service';
import { HttpResponseService } from '../services/httpresponse-service';

import { Quote } from '../model/quote';
import { CmhUser } from '../model/cmhuser';
import { Constants } from '../model/constants';
import { AuditLog } from '../model/audit-log';
import { PagerInfo } from '../model/pager-info';

@Component({
  selector: 'my-audit-history',
  template: require('./audit-history.component.html'),
  styles: [require('./audit-history.component.scss')]
})
export class AuditHistoryComponent implements OnInit, OnDestroy {
  quoteId: number;
  currentPricingView: number;

  mchBaseUrl: string;
  tabName: string = 'Quote';

  quotesTab: boolean = true;
  adjustmentsTab: boolean = false;
  customerTab: boolean = false;
  optionsTab: boolean = false;
  orderInfoTab: boolean = false;
  approvalsTab: boolean = false;
  displayNoMatches: boolean = false;
  hideBackgroundData: boolean = false;
  stateIsInvalid: boolean = false;

  auditLogs: AuditLog[];
  pagedLogs: AuditLog[];
  config: ToasterConfig = new ToasterConfig({
    positionClass:
      this.browserWidthService.getDisplayMode().width.innerWidth < 769
        ? 'toast-bottom-full-width'
        : 'toast-bottom-right'
  });
  quote: Quote;
  cmhUser: CmhUser;
  pager: PagerInfo;
  subscription: Subscription = new Subscription();

  tabObjects = [
    {
      tabName: 'Quote',
      tabTitle: 'Quote',
      tableName: 'Quotes',
      isDefault: true,
      sortSequence: 1,
      isActive: true
    },
    {
      tabName: 'Customer',
      tabTitle: 'Customer',
      tableName: 'Customer',
      isDefault: false,
      sortSequence: 2,
      isActive: false
    },
    {
      tabName: 'Options',
      tabTitle: 'Selected Options',
      tableName: 'SelectedOptions',
      isDefault: false,
      sortSequence: 3,
      isActive: false
    },
    {
      tabName: 'OrderInformation',
      tabTitle: 'Order Information',
      tableName: 'OrderInformation',
      isDefault: false,
      sortSequence: 4,
      isActive: false
    },
    {
      tabName: 'Markups',
      tabTitle: 'Adjustments',
      tableName: 'Adjustments',
      isDefault: false,
      sortSequence: 5,
      isActive: false
    },
    {
      tabName: 'ApprovalHistory',
      tabTitle: 'Approval History',
      tableName: 'ApprovalHistory',
      isDefault: false,
      sortSequence: 6,
      isActive: false
    }
  ];

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public spinner: LoadingSpinnerService,
    public pageTitleService: PageTitleService,
    public auditHistoryService: AuditHistoryService,
    public quoteInfoService: QuoteInfoService,
    public toasterService: ToasterService,
    public applicationStateService: ApplicationStateService,
    public modalDialogService: ModalDialogService,
    public cookieService: CookieService,
    public quoteService: QuoteService,
    public currentUserService: CurrentUserService,
    public formatUtilities: FormatUtilities,
    public pagerService: PagerService,
    public browserWidthService: BrowserWidthService,
    private httpResponseService: HttpResponseService
  ) {
    const routeParamsSub = this.route.params.subscribe(x => {
      this.quoteId = Number(x['quoteId']);
      this.quoteInfoService.quoteId$.next(this.quoteId);
      this.loadAuditHistory();
    });
    this.subscription.add(routeParamsSub);

    const currentUserSub = this.currentUserService.currentUser$.subscribe(x => {
      this.cmhUser = x;
    });
    this.subscription.add(currentUserSub);

    const currentPricingViewSub = this.quoteInfoService.currentPricingView$.subscribe(
      x => {
        this.currentPricingView = x;
      }
    );
    this.subscription.add(currentPricingViewSub);

    this.pageTitleService.pageTitle.next({
      title: Constants.CmhConnectionTitle
    });
    this.mchBaseUrl = this.cookieService.get('mchBaseUrl');
  }

  ngOnInit() {
    this.quoteInfoService.canAccessQuote$.subscribe(x => {
      this.hideBackgroundData = !x;
    });
    if (this.quoteId) {
      this.applicationStateService
        .refreshStateFromDB(this.quoteId, true)
        .subscribe(
          dbQuote => {
            if (dbQuote !== undefined && dbQuote !== null) {
              this.pageTitleService.pageTitle.next({
                title: Constants.CmhConnectionTitle,
                subTitle:
                  Constants.CurrentQuoteSubTitle +
                  (dbQuote.iseriesQuoteNumber > 0 && dbQuote.QuoteStatusesID > 0
                    ? dbQuote.iseriesQuoteNumber
                    : this.quoteId)
              });
              this.quote = dbQuote;
              this.quoteInfoService.quote$.next(dbQuote);
              const canAccessQuoteSub = this.quoteInfoService.canAccessQuote$.subscribe(
                canAccessQuote => {
                  if (canAccessQuote === true) {
                    if (
                      dbQuote.productLineId === undefined ||
                      dbQuote.productLineId === null ||
                      dbQuote.productLineId === 0
                    ) {
                      this.stateIsInvalid = true;
                      this.modalDialogService
                        .showMessage({
                          headerText: Constants.NoProductLineHeader,
                          message: Constants.NoProductLineMessage
                        })
                        .subscribe(messageReply => {
                          this.redirectRequired(false);
                        });
                    } else {
                      this.loadAuditHistory();
                    }
                  } else {
                    this.spinner.hideSpinner();
                  }
                },
                error => {
                  this.spinner.hideSpinner();
                },
                () => {
                  this.spinner.hideSpinner();
                }
              );
              this.subscription.add(canAccessQuoteSub);
            } else {
              this.stateIsInvalid = true;
              this.modalDialogService
                .showMessage({
                  headerText: 'Audit History',
                  message: Constants.QuoteNotFoundMessage.replace(
                    '#quoteid#',
                    this.quoteId.toString()
                  )
                })
                .subscribe(messageReply => {
                  this.redirectRequired(true);
                });
            }
          },
          error => {
            this.spinner.hideSpinner();
            this.httpResponseService.httpErrorResponse$.next(error);
          }
        );
    } else {
      this.modalDialogService
        .showMessage({
          headerText: 'Audit History',
          message: Constants.QuoteNotFoundMessage.replace(
            '#quoteid#',
            this.quoteId.toString()
          )
        })
        .subscribe(messageReply => {
          this.redirectRequired(true);
        });
    }
    //  this.sortTabs();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get tableWidth() {
    const elem = document.getElementById('main-content-wrapper');
    return `${elem.clientWidth - 30}px`;
  }

  /// Loads the AuditHistory for the given quote
  loadAuditHistory() {
    this.spinner.showSpinner();
    if (
      this.quoteId !== undefined &&
      this.quoteId !== null &&
      this.quoteId !== 0
    ) {
      this.auditHistoryService
        .getAuditHistory(
          this.quoteId,
          this.quotesTab
            ? 'Quotes'
            : this.customerTab
            ? 'Customer'
            : this.adjustmentsTab
            ? 'Adjustments'
            : this.orderInfoTab
            ? 'OrderInformation'
            : this.optionsTab
            ? 'SelectedOptions'
            : this.approvalsTab
            ? 'ApprovalHistory'
            : ''
        )
        .subscribe(
          data => {
            this.browserWidthService.setScroll(false);
            if (data !== undefined && data !== null) {
              this.auditLogs = data;
              this.pager = new PagerInfo();
              this.displayNoMatches = false;
            } else {
              this.pager = new PagerInfo();
              this.displayNoMatches = true;
            }
          },
          error => {
            this.handleSubscriptionError(error);
          },
          () => {
            if (this.auditLogs !== undefined && this.auditLogs !== null) {
              this.setPage(1, this.auditLogs);
            } else {
              this.setPage(1, new Array<AuditLog>());
            }
          }
        );
    } else {
      this.pager = new PagerInfo();
      this.displayNoMatches = true;
      this.setPage(1, new Array<AuditLog>());
      this.spinner.hideSpinner();
    }
  }

  /// Changes the tab and loads the new set of data
  changeTab(tabName: string) {
    this.tabObjects.forEach(tab => {
      if (tab.tabName === tabName) {
        tab.isActive = true;
      } else {
        tab.isActive = false;
      }
    });

    if (tabName !== undefined && tabName !== null && tabName !== '') {
      this.tabName = tabName;
      switch (tabName.toUpperCase()) {
        case 'QUOTE':
          this.quotesTab = true;
          this.customerTab = false;
          this.adjustmentsTab = false;
          this.orderInfoTab = false;
          this.optionsTab = false;
          this.approvalsTab = false;
          break;

        case 'CUSTOMER':
          this.quotesTab = false;
          this.customerTab = true;
          this.adjustmentsTab = false;
          this.orderInfoTab = false;
          this.optionsTab = false;
          this.approvalsTab = false;
          break;

        case 'MARKUPS':
          this.quotesTab = false;
          this.customerTab = false;
          this.adjustmentsTab = true;
          this.orderInfoTab = false;
          this.optionsTab = false;
          this.approvalsTab = false;
          break;

        case 'ORDERINFORMATION':
          this.quotesTab = false;
          this.customerTab = false;
          this.adjustmentsTab = false;
          this.orderInfoTab = true;
          this.optionsTab = false;
          this.approvalsTab = false;
          break;

        case 'OPTIONS':
          this.quotesTab = false;
          this.customerTab = false;
          this.adjustmentsTab = false;
          this.orderInfoTab = false;
          this.optionsTab = true;
          this.approvalsTab = false;
          break;

        case 'APPROVALHISTORY':
          this.quotesTab = false;
          this.customerTab = false;
          this.adjustmentsTab = false;
          this.orderInfoTab = false;
          this.optionsTab = false;
          this.approvalsTab = true;
          break;

        default:
          this.quotesTab = true;
          this.customerTab = false;
          this.adjustmentsTab = false;
          this.orderInfoTab = false;
          this.optionsTab = false;
          this.approvalsTab = false;
          break;
      }

      this.loadAuditHistory();
    }
  }

  /// Redirects the user back to the customer-info page
  redirectRequired(newQuotesRedirect: boolean) {
    if (newQuotesRedirect) {
      this.router.navigate([Constants.NewQuotesUrl]);
    } else {
      window.location.href = `${this.mchBaseUrl}/${[
        Constants.ProductLineUrl.replace('#quoteid#', this.quoteId.toString())
      ]}`;
    }
  }

  /// Determines whether to show the field based on pricing view
  showFieldCheck(item: AuditLog): boolean {
    return (item.FieldName.toUpperCase().includes('OVERRIDE') ||
      item.FieldName.toUpperCase().includes('PRICE')) &&
      this.currentPricingView === 3
      ? false
      : true;
  }

  /// Resets the pages to a new list of quotes
  setPage(page: number, logs: AuditLog[]) {
    this.spinner.showSpinner();
    if (logs !== undefined && logs !== null && logs.length > 0) {
      this.displayNoMatches = false;
      this.pager = this.pagerService.getPager(logs, page, 21);
      this.pagedLogs = this.pager.pages[page - 1].Data;
    } else {
      this.displayNoMatches = true;
      this.pager = this.pagerService.getPager(new Array<Quote>(), page, 21);
      this.pagedLogs = new Array<AuditLog>();
    }
    this.spinner.hideSpinner();
  }

  /// Navigates to the next page of quotes
  navigatePages(page: number) {
    this.spinner.showSpinner();
    if (
      this.auditLogs !== undefined &&
      this.auditLogs !== null &&
      this.auditLogs.length > 0 &&
      this.pager !== undefined &&
      this.pager !== null &&
      this.pager.pages[page - 1].Data !== undefined &&
      this.pager.pages[page - 1].Data !== null
    ) {
      this.pager.currentPage = page;
      this.pagedLogs = this.pager.pages[page - 1].Data;
    } else {
      this.pagedLogs = new Array<AuditLog>();
    }
    this.spinner.hideSpinner();
  }

  handleSubscriptionError(error: any) {
    this.spinner.hideSpinner();
  }

  /// Sorts the Plants
  sortTabs() {
    if (
      this.tabObjects !== undefined &&
      this.tabObjects !== null &&
      this.tabObjects.length > 1
    ) {
      this.tabObjects.sort((a, b) => {
        return a.sortSequence - b.sortSequence;
      });
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/audit-history/audit-history.component.ts